import { useEffect, useRef, useState } from 'react'
import { Loader2, MessageSquare, Trash2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/utils/cn'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import { formatRelativeTime, formatUserDisplayName } from '@/utils/formatters'
import {
  useLineItemComments,
  useAddLineItemComment,
  useDeleteLineItemComment,
} from '@/hooks/useLineItemComments'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { toast } from '@/stores/useToastStore'
import type { LineItemComment } from '@/api/lineItemComments'
import type { DagRowRead } from '@/types/dag'

const COMMENT_MAX_LENGTH = 2000

interface LineItemCommentsDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  lineRefId: string | null
  row: DagRowRead | null
}

function CommentItem({
  comment,
  canDelete,
  onDelete,
  isDeleting,
}: {
  comment: LineItemComment
  canDelete: boolean
  onDelete: () => void
  isDeleting: boolean
}) {
  const authorName = formatUserDisplayName(
    comment.author
      ? { id: comment.author.id, display_name: comment.author.display_name }
      : null
  )
  const authorId = comment.author?.id ?? comment.id
  const avatarColor = getAvatarColor(authorId)
  const initials = getInitials(comment.author?.display_name ?? null, authorName)
  const isOptimistic = comment.id.startsWith('optimistic-')

  return (
    <div className={cn('flex gap-3 group', isOptimistic && 'opacity-60')}>
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback
          className={cn(
            avatarColor.bg,
            avatarColor.text,
            'text-xs font-medium'
          )}
        >
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium truncate">{authorName}</span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {formatRelativeTime(comment.created_at)}
          </span>
          {canDelete && !isOptimistic && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={onDelete}
              disabled={isDeleting}
              aria-label="Delete comment"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <p className="text-sm whitespace-pre-wrap mt-0.5 break-words">
          {comment.content}
        </p>
      </div>
    </div>
  )
}

export function LineItemCommentsDrawer({
  open,
  onOpenChange,
  lineRefId,
  row,
}: LineItemCommentsDrawerProps) {
  const [content, setContent] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { userId } = useCurrentUser()

  const { data, isLoading, isError, error } = useLineItemComments(
    open ? lineRefId : null
  )
  const addMutation = useAddLineItemComment(lineRefId ?? '')
  const deleteMutation = useDeleteLineItemComment(lineRefId ?? '')

  useEffect(() => {
    if (!open) setContent('')
  }, [open])

  useEffect(() => {
    if (isError && error) toast.error(error.message)
  }, [isError, error])

  const comments = data?.data ?? []
  const sorted = [...comments].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )

  const sku = (row?.cells?.['plan_sku_num'] as string) ?? null
  const description = (row?.cells?.['description'] as string) ?? null
  const rowIndex = row?.row_index != null ? `Line ${row.row_index + 1}` : null
  const subtitleParts = [sku, description, rowIndex].filter(Boolean)

  const canSubmit =
    content.trim().length > 0 &&
    content.length <= COMMENT_MAX_LENGTH &&
    !addMutation.isPending &&
    !!lineRefId

  const handleSubmit = () => {
    if (!canSubmit) return
    const value = content.trim()
    setContent('')
    addMutation.mutate(
      { content: value },
      {
        onError: (err: Error) => {
          toast.error(err.message || 'Failed to post comment')
          setContent(value)
        },
      }
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleDelete = (commentId: string) => {
    deleteMutation.mutate(
      { commentId },
      {
        onError: (err: Error) => {
          toast.error(err.message || 'Failed to delete comment')
        },
      }
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col gap-0 p-0 sm:max-w-md w-full"
      >
        <SheetHeader className="border-b p-4">
          <SheetTitle>Comments</SheetTitle>
          {subtitleParts.length > 0 && (
            <SheetDescription className="truncate">
              {subtitleParts.join(' · ')}
            </SheetDescription>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <MessageSquare className="h-10 w-10 mb-3" />
              <p className="text-sm font-medium">No comments yet</p>
              <p className="text-xs mt-1">Be the first to comment.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {sorted.map(comment => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  canDelete={!!userId && comment.author?.id === userId}
                  onDelete={() => handleDelete(comment.id)}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>

        <div className="border-t p-4 space-y-2">
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={e => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment..."
            maxLength={COMMENT_MAX_LENGTH}
            rows={3}
            className="resize-none"
            disabled={!lineRefId}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Enter to send · Shift+Enter for newline
            </span>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className=""
            >
              {addMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Post'
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
